import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { UseOutletContextQuote } from './Quote';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { QuoteDA } from '../../../../services/admin/quote.service';
import { Error } from '../../../../models/error.model';
import { Message } from '../../../../models/message.model';
import { Quote } from '../../../../models/quote.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { legendInvalid } from '../../../../utils/legend.data.util';
import { isValidObjectAttributes } from '../../../../scripts/compare.object.script';
import { getError, getMessage } from '../../../../languages/translations/response';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { normalizeContactData } from '../../../../scripts/format.value.script';
import { foreignCurrencyDefault, foreignCurrencyFromRecord, foreignCurrencyPayload, isForeignCurrencyValid, ForeignCurrencyState } from '../../../../components/element/InputForeignCurrency';

import AppSaleFormReference from '../component/SaleFormReference';
import AppSaleFormRequirements from '../component/SaleFormRequirements';
import AppSaleFormSignatures from '../component/SaleFormSignatures';
import AppPreloader from '../../../../components/Preloader';

export interface QuoteReferencePageProps {};

let errorResponse: Error, messageResponse: Message, quoteResponse: Quote;

const QuoteReferencePage: React.FunctionComponent<QuoteReferencePageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, treasuryAccountsForCompany, contactsForCompany, productsForCompany, servicesForCompany} = UseOutletContextQuote()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [customer, setCustomer] = useState({value: '', valid: false, object: null as any})
  const [concerning, setConcerning] = useState({value: '', valid: false})
  const [date, setDate] = useState({value: '', valid: false})
  const [validUntil, setValidUntil] = useState({value: '', valid: false})
  const [deadline, setDeadline] = useState({value: '', valid: false, object: null as any})
  const [currency, setCurrency] = useState({value: '', valid: false, object: null as any})
  const [treasuryAccount, setTreasuryAccount] = useState({value: '', valid: false, object: null as any})
  const [language, setLanguage] = useState({value: '', valid: false})
  const [discountType, setDiscountType] = useState({value: '', valid: false})
  const [discountValue, setDiscountValue] = useState({value: '', valid: false})
  const [requirements, setRequirements] = useState({list: false, value: [] as {type: string, description: string, contents: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string}[]}[], valid: [] as {type: boolean, description: boolean, content: boolean, contents: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean}[]}[]})
  const [signatures, setSignatures] = useState({list: true, value: [] as string[], valid: [] as boolean[]})
  const [foreignCurrency, setForeignCurrency] = useState<ForeignCurrencyState>(foreignCurrencyDefault)
  const [quote, setQuote] = useState<Quote | null>(null)

  const loadQuote = async (id_company: string, id_quote: string) => {
    await QuoteDA.getQuote(id_company, id_quote).then( (response) => {
      if (response.status === 200) {
        quoteResponse = response.data

        let tempRequirementsValue: {type: string, description: string, contents: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string}[]}[] = []
        let tempRequirementsValid: {type: boolean, description: boolean, content: boolean, contents: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean}[]}[] = []
        let tempSignaturesValue: string[] = []
        let tempSignaturesValid: boolean[] = []

        for (let requirement of quoteResponse.requirements) {
          let newRequirimentValue: {type: string, description: string, contents: any[]} = {
            type: requirement.type,
            description: requirement.description,
            contents: []
          }
          let newRequirementValid: {type: boolean, description: boolean, content: boolean, contents: any[]} = {
            type: true,
            description: true,
            content: true,
            contents: []
          }

          for (let content of requirement.contents) {
            let newContentValue: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string} = {
              detail: content.detail,
              reference: content.reference,
              quantity: content.quantity.toString(),
              price_unit: content.price_unit.toString(),
              tax_included: content.tax_included,
              tax_code: content.tax_code,
              tax_rate: content.tax_rate,
              rebate_type: content.rebate ? content.rebate.type : '',
              rebate_value: content.rebate ? content.rebate.value : 0,
              rebate_quantity: content.rebate ? content.rebate.quantity : 0,
              rebate_amount: content.rebate_amount.toString(),
              unit: content.unit
            }
            let newContentValid: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean} = {
              detail: true,
              quantity: true,
              price_unit: true,
              tax: true,
              rebate_amount: true,
              unit: true
            }

            newRequirimentValue.contents.push(newContentValue)
            newRequirementValid.contents.push(newContentValid)
          }

          tempRequirementsValue.push(newRequirimentValue)
          tempRequirementsValid.push(newRequirementValid)
        }

        for (let signature of quoteResponse.signatures) {
          tempSignaturesValue.push(signature)
          tempSignaturesValid.push(true)
        }

        normalizeContactData(quoteResponse.customer)
        setQuote(quoteResponse)
        setCustomer({value: quoteResponse.customer.data.id, object: quoteResponse.customer, valid: true})
        setConcerning({value: quoteResponse.concerning, valid: true})
        setDate({value: format((new Date()), 'yyyy-MM-dd'), valid: true})
        setValidUntil({value: format((new Date()), 'yyyy-MM-dd'), valid: true})
        setCurrency({value: quoteResponse.currency.code, object: quoteResponse.currency, valid: true})
        setForeignCurrency(foreignCurrencyFromRecord(quoteResponse))
        setTreasuryAccount({value: quoteResponse.treasury_account.id, object: quoteResponse.treasury_account, valid: true})
        setLanguage({value: quoteResponse.language, valid: true})
        setDiscountType({value: 'none', valid: true})
        setDiscountValue({value: '', valid: true})
        setRequirements({list: true, value: tempRequirementsValue, valid: tempRequirementsValid})
        setSignatures({list: true, value: tempSignaturesValue, valid: tempSignaturesValid})
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitQuoteCreate = async () => {
    setLoadIndicator('on')

    let requerimentsIsValid: boolean = requirements.valid.every((item) => isValidObjectAttributes(item))
    let signaturesIsValid: boolean = signatures.valid.every((item) => (item === true))

    if (companyForUser && quote && concerning.valid && date.valid && validUntil.valid && currency.valid && treasuryAccount.valid && language.valid && requirements.list && signatures.list && requerimentsIsValid && signaturesIsValid && isForeignCurrencyValid(foreignCurrency)) {
      let validUntilDate = `${validUntil.value}T23:59:59.999Z`

      await QuoteDA.postQuote(companyForUser.id, customer.object, concerning.value, date.value, validUntilDate, companyForUser.currency, foreignCurrencyPayload(foreignCurrency), treasuryAccount.object, language.value, requirements.value, signatures.value, quote.company, quote.quote_number).then( (response) => {
        if (response.status === 201) {
          messageResponse = response.data

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedRedirectingToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate('/main/sale/quote/list', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!customer.value) legendInvalid('legend-contact-required')
        if (!concerning.value) legendInvalid('legend-concerning-required')
        if (!date.value) legendInvalid('legend-date-required')
        if (!validUntil.value) legendInvalid('legend-valid-until-required')
        if (!currency.value) legendInvalid('legend-currency-required')
        if (!treasuryAccount.value) legendInvalid('legend-treasury-account-required')
        if (!language.value) legendInvalid('legend-language-required')
        if (!discountValue.value) legendInvalid('legend-discount-required')
        if (!requirements.value) legendInvalid('legend-requirements-required')
        if (!requirements.value) legendInvalid('legend-requirements-required')
        if (!requerimentsIsValid) {
          for (let i = 0; i < requirements.value.length; i++) {
            if (requirements.value[i].contents.length === 0) {
              legendInvalid(`legend-requirement-contents-required${suffixLegend(i)}`)
            } else {
              for (let j = 0; j < requirements.value[i].contents.length; j++) {
                if (!requirements.value[i].contents[j].detail) legendInvalid(`legend-requirement-content-detail-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].unit) legendInvalid(`legend-requirement-content-unit-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].quantity) legendInvalid(`legend-requirement-content-quantity-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].price_unit) legendInvalid(`legend-requirement-content-price-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].tax_rate) legendInvalid(`legend-requirement-content-tax-required${suffixLegend(i, j)}`)
                if (!requirements.value[i].contents[j].rebate_amount) legendInvalid(`legend-requirement-content-rebate-required${suffixLegend(i, j)}`)
              }
            }
          }
        }
        if (!signaturesIsValid) {
          for (let i = 0; i < signatures.value.length; i++) {
            if (!signatures.value[i]) legendInvalid(`legend-signature-required${suffixLegend(i)}`)
          }
        }
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadQuote(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.quotes, branch: lang.labels.createQuote}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && treasuryAccountsForCompany && contactsForCompany && productsForCompany && servicesForCompany && quote
        ?
          <div className="form">
            <AppSaleFormReference sale={"quote"} record={quote} customer={customer} concerning={concerning} date={date} validUntil={validUntil} deadline={deadline} currency={currency} treasuryAccount={treasuryAccount} language={language} discountType={discountType} discountValue={discountValue} foreignCurrency={foreignCurrency} setCustomer={setCustomer} setConcerning={setConcerning} setDate={setDate} setValidUntil={setValidUntil} setDeadline={setDeadline} setCurrency={setCurrency} setTreasuryAccount={setTreasuryAccount} setLanguage={setLanguage} setDiscountType={setDiscountType} setDiscountValue={setDiscountValue} setForeignCurrency={setForeignCurrency}></AppSaleFormReference>
            <AppSaleFormRequirements date={date.value} currency={companyForUser.currency} requirements={requirements} setRequirements={setRequirements} foreignCurrency={foreignCurrency}></AppSaleFormRequirements>
            <AppSaleFormSignatures signatures={signatures} setSignatures={setSignatures}></AppSaleFormSignatures>
            <div className="d-flex flex-stack flex-wrap flex-md-nowrap bg-light-primary border border-primary border-dashed rounded h-100 p-5">
              <div className="notice d-flex align-items-center mb-5 mb-md-0 me-0 me-md-5">
                <span className="svg-icon svg-icon-3tx svg-icon-primary me-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                    <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                  </svg>
                </span>    
                <div className="d-grid">
                  <span className="text-start text-dark fw-bolder fs-5">{lang.labels.processOfCreatingAQuoteFromAnotherQuoteCompleted}</span>
                  <span className="text-justify text-muted fw-bold">
                    {lang.labels.clickOn}
                    <span className="text-primary text-lowercase fw-bolder mx-1">{lang.labels.createQuote}</span>
                    {lang.labels.ifYouWantToSaveTheNewQuoteWithTheChangesMade}
                  </span>
                </div>
              </div>
              <button className="btn btn-lg btn-primary fw-bolder w-100 w-md-300px" type="button" data-kt-indicator={loadIndicator} onClick={submitQuoteCreate}>
                <span className="indicator-label text-uppercase text-nowrap">{lang.labels.createQuote}</span>
                <span className="indicator-progress">
                {lang.labels.pleaseWait}
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>
            </div>
          </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default QuoteReferencePage;
